import type { MobileBatchSurface } from '@/stores/mobileBatchSelectionStore'

type EditableItem = {
  clientId: string
  canEdit?: boolean | null
}

type OwnableCollection = {
  clientId: string
  isOwner?: boolean | null
}

export interface MobileBatchPermissionInput {
  surface: MobileBatchSurface | null
  selectedTours: EditableItem[]
  selectedPOIs: EditableItem[]
  selectedPhotos: EditableItem[]
  selectedCollections: OwnableCollection[]
  /** Owner of the active collection ('collection-detail' surface). Owners may unlink (but not
   * necessarily edit/delete) any member's tour/POI — see validate_workspace_owner_or_creator. */
  isCollectionOwner?: boolean
  permissions: {
    deleteTour: boolean
    deleteWaypoint: boolean
    deletePhoto: boolean
  }
}

export interface MobileBatchPermissionState {
  canDeleteSelectedTours: boolean
  canDeleteSelectedPOIs: boolean
  canDeleteSelectedPhotos: boolean
  canDeleteSelectedCollections: boolean
  canAddToCollection: boolean
  addToCollectionDisabledReason?: string
  deleteHasPermissionConflict: boolean
  removeHasPermissionConflict: boolean
}

const canDeleteTour = (
  surface: MobileBatchSurface | null,
  tour: EditableItem,
  deleteTourPermission: boolean,
) => {
  if (surface === 'library') return tour.canEdit !== false
  return deleteTourPermission && tour.canEdit !== false
}

export const resolveMobileBatchPermissionState = (
  input: MobileBatchPermissionInput,
): MobileBatchPermissionState => {
  const canDeleteSelectedTours = input.selectedTours.every((tour) =>
    canDeleteTour(input.surface, tour, input.permissions.deleteTour),
  )
  const canDeleteSelectedPOIs =
    input.selectedPOIs.length === 0 ||
    (input.permissions.deleteWaypoint === true &&
      input.selectedPOIs.every((poi) => poi.canEdit !== false))
  const canDeleteSelectedPhotos =
    input.selectedPhotos.length === 0 ||
    (input.permissions.deletePhoto === true &&
      input.selectedPhotos.every((photo) => photo.canEdit !== false))
  const canDeleteSelectedCollections = input.selectedCollections.every(
    (collection) => collection.isOwner === true,
  )
  const canAddToCollection =
    input.selectedPhotos.length === 0 && input.selectedTours.length + input.selectedPOIs.length > 0
  const addToCollectionDisabledReason =
    input.selectedPhotos.length > 0 ? 'Photos cannot be added to collections yet' : undefined

  const deleteHasPermissionConflict =
    input.surface === 'collections-overview'
      ? input.selectedCollections.length > 0 && !canDeleteSelectedCollections
      : !canDeleteSelectedTours || !canDeleteSelectedPOIs || !canDeleteSelectedPhotos

  // Unlinking is owner-or-creator (validate_workspace_owner_or_creator), not creator-only: the
  // collection owner may unlink anyone's item, so their canEdit (a creator-only signal) doesn't
  // apply to them.
  const canRemoveItem = (item: EditableItem) =>
    input.isCollectionOwner === true || item.canEdit !== false
  const removeHasPermissionConflict =
    input.surface === 'collection-detail' &&
    ((input.selectedTours.length > 0 &&
      (input.permissions.deleteTour !== true || !input.selectedTours.every(canRemoveItem))) ||
      (input.selectedPOIs.length > 0 &&
        (input.permissions.deleteWaypoint !== true || !input.selectedPOIs.every(canRemoveItem))))

  return {
    canDeleteSelectedTours,
    canDeleteSelectedPOIs,
    canDeleteSelectedPhotos,
    canDeleteSelectedCollections,
    canAddToCollection,
    addToCollectionDisabledReason,
    deleteHasPermissionConflict,
    removeHasPermissionConflict,
  }
}
